// Copyright (c) 2018 Cigent Technology Inc.

import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { D3eService } from '../../../../services/d3e-service';
import { NotificationService } from '../../../../services/notification-service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { HttpStatusCode } from '@angular/common/http';
import { withLoading } from '../../../../shared/misc';
import { CustomValidators } from '../../../shared/validators/custom-validators';
import { IAddFdeSerialData, IAddWorkstationsRequest, SerialLicensingOption } from '../../../../types/licensedDrives';
import { isMSP } from '../../../../shared/permissions-helper';

@Component({
    selector: 'app-add-workstation-dialog',
    templateUrl: './add-workstation-dialog.component.html',
    styleUrl: './add-workstation-dialog.component.scss',
    standalone: false
})
export class AddWorkstationDialogComponent implements OnInit {
    protected readonly isMSP = isMSP;
    form: UntypedFormGroup;
    @Output() workstationIdSubmitted = new EventEmitter<SerialLicensingOption>();
    loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    error_occurred: string = null;
    errorMessage: string = '';
    serialLicensingOption = SerialLicensingOption.NO_LICENSE;
    serialLicensingOptionType = SerialLicensingOption;

    constructor(@Inject(MAT_DIALOG_DATA) public data: IAddFdeSerialData,
                private readonly formBuilder: UntypedFormBuilder,
                private readonly dialogRef: MatDialogRef<AddWorkstationDialogComponent>,
                private readonly d3eService: D3eService,
                private notification: NotificationService,
                private translate: TranslateService) {
        this.form = this.formBuilder.group({
            serial: ['', [Validators.required, CustomValidators.uniqueSerialsValidator()]],
            licensingOption: [this.serialLicensingOption, [Validators.required]],
            maintenanceExpiration: [data.maintenanceExpiration, Validators.required]
        });
    }

    ngOnInit(): void {
        const serialInputControl = this.form.get('serial');
        const licensingControl = this.form.get('licensingOption');
        const expirationControl = this.form.get('maintenanceExpiration');

        const updateExpirationState = (value: SerialLicensingOption) => {
            if (value === SerialLicensingOption.NO_LICENSE) {
                expirationControl?.setValue(this.data.maintenanceExpiration);
                expirationControl?.disable();
                expirationControl?.clearValidators();
                expirationControl?.markAsUntouched();
                expirationControl?.markAsPristine();
                serialInputControl?.setValidators([
                    Validators.required,
                    CustomValidators.uniqueSerialsValidator()
                ]);
                if (!serialInputControl?.value) {
                    serialInputControl?.markAsTouched();
                }
            } else if (value === SerialLicensingOption.ALL_EXISTING_LICENSED || value === SerialLicensingOption.UNLICENSED_SERIALS_LICENSED) {
                serialInputControl?.setValidators([
                    CustomValidators.uniqueSerialsValidator()
                ]);
                serialInputControl?.markAsUntouched();
                serialInputControl?.markAsPristine();

                expirationControl?.enable();
                expirationControl?.setValidators(Validators.required);
                if (!expirationControl?.value) {
                    expirationControl?.markAsTouched();
                }
            } else {
                serialInputControl?.setValidators([
                    Validators.required,
                    CustomValidators.uniqueSerialsValidator()
                ]);
                if (!serialInputControl?.value) {
                    serialInputControl?.markAsTouched();
                }

                expirationControl?.enable();
                expirationControl?.setValidators(Validators.required);
                if (!expirationControl?.value) {
                    expirationControl?.markAsTouched();
                }
            }
            serialInputControl?.updateValueAndValidity();
            expirationControl?.updateValueAndValidity();
        };

        updateExpirationState(licensingControl?.value);
        licensingControl?.valueChanges.subscribe((value) => updateExpirationState(value));
    }

    addWorkstation(serials: string, licensingOption: SerialLicensingOption, maintenanceExpiration: string,  notify = false) {
        this.error_occurred = null;
        const workstation_ids: string[] = serials.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
        const request: IAddWorkstationsRequest = {
            serials: workstation_ids,
            subscription_id: this.data.subscriptionId,
            licensing_option: licensingOption,
            maintenance_date: maintenanceExpiration
        }
        this.d3eService.addFdeWorkstationIds(this.data.selectedOrgId, request).pipe(
            withLoading(isLoading => this.loading$.next(isLoading))
        ).subscribe({
                next: () => {
                    this.workstationIdSubmitted.emit(licensingOption);
                    this.form.reset();
                    if (notify) {
                        let message;
                        if (licensingOption === SerialLicensingOption.NO_LICENSE) {
                                const serialsCount = workstation_ids.length;
                                message = this.translate.instant('assign-fde.workstation-ids-added', {count: serialsCount});
                        } else {
                                const serialsLicensedCount = workstation_ids.length;
                                message = this.translate.instant('assign-fde.workstation-ids-licensed', {count: serialsLicensedCount});
                        }
                        switch (licensingOption) {
                            case SerialLicensingOption.NO_LICENSE:
                                const serialsCount = workstation_ids.length;
                                message = this.translate.instant('assign-fde.workstation-ids-added', {count: serialsCount});
                                break;
                            case SerialLicensingOption.NEW_SERIALS_LICENSED:
                                const serialsLicensedCount = workstation_ids.length;
                                message = this.translate.instant('assign-fde.workstation-ids-licensed', {count: serialsLicensedCount});
                                break;
                            case SerialLicensingOption.UNLICENSED_SERIALS_LICENSED:
                                const serialsUnassignedCount = workstation_ids.length + (this.data.totalSerials - this.data.totalLicensed);
                                message = this.translate.instant('assign-fde.workstation-ids-licensed', {count: serialsUnassignedCount});
                                break;
                            case SerialLicensingOption.ALL_EXISTING_LICENSED:
                                const allCount = workstation_ids.length + this.data.totalLicensed;
                                message = this.translate.instant('assign-fde.workstation-ids-licensed', {count: allCount});
                                break;
                        }
                        this.notification.showSuccess(message);
                        this.dialogRef.close();
                    }
                },
                error: response => {
                    this.form.enable();
                    if (response) {
                        if (response.status === HttpStatusCode.Conflict) {
                            this.error_occurred = this.translate.instant('assign.fde.add-dialog.fde_serials-already-exits', {serials: response?.error['reason']});
                        } else if ( (response.error['msg'] || '').length > 0) {
                            switch (response.error['status']) {
                                case 'duplicate-serials':
                                    this.error_occurred = 'assign-licenses-add-ssd-serial-dialog.already-exists';
                                    this.errorMessage = response.error['msg'];
                                    break;
                                case 'serials-too-long':
                                    this.error_occurred = 'assign-licenses-add-ssd-serial-dialog.invalid-serial-length';
                                    this.errorMessage = response.error['msg'];
                                    break;
                                default:
                                    this.error_occurred = 'ERROR_OCCURRED_UNKNOWN';
                                    break;
                            }
                        } else {
                            this.error_occurred = this.translate.instant('ERROR_OCCURRED_UNKNOWN');
                        }
                    }
                }
            }
        );
    }

    onCancelClick() {
        this.dialogRef.close();
    }

    onSaveClick() {
        this.addWorkstation(this.form.value.serial, this.form.value.licensingOption, this.form.value.maintenanceExpiration, true);
    }

    onEnterPressed(event) {
        if (this.form.value.serial != null &&
            this.form.value.serial.length &&
            this.form.value.serial.indexOf(',') == -1) {
            event.stopPropagation()
            event.preventDefault()
            this.addWorkstation(this.form.value.serial, this.form.value.licensingOption, this.form.value.maintenanceExpiration, true);
        }
    }

    onDataChanged() {
        this.error_occurred = null;
    }

}
